import React, { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatCurrency } from '@/lib/utils'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  AreaChart, Area,
} from 'recharts'
import { TrendingUp, Target, Layers, BarChart3 } from 'lucide-react'

/**
 * Pipeline Forecast (Sales forecasting Slice 1 CP2). Open pipeline by stage —
 * count + raw value + a weighted forecast (Σ deal.value × stage win_probability).
 * Reuses the Analytics.tsx chrome (StatCard / Section / CustomTooltip / recharts).
 *
 * Grouping key = HYBRID: a deal matches its stage by stage_id when that id points
 * at a current pipeline_stage, else by stage_name (case-insensitive). Real data
 * carries stage via the denormalized stage_name (stage_id is NULL on ~93% of prod
 * deals), so stage_id-only grouping would orphan nearly everything into stage 0
 * and wildly understate the weighted forecast. Unmatched deals → "Unassigned"
 * (excluded from the weighted total, never silently mis-weighted).
 */

const COLORS = {
  primary: 'var(--accent)',
  neon: 'var(--positive)',
  amber: 'var(--warning)',
}

function StatCard({ label, value, sub, color, icon: Icon }: any) {
  return (
    <motion.div whileHover={{ y: -2 }}
      style={{ background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '18px 20px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: -24, right: -24, width: 80, height: 80, background: `radial-gradient(circle, color-mix(in srgb, ${color} 13%, transparent) 0%, transparent 70%)`, pointerEvents: 'none' }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: `color-mix(in srgb, ${color} 9%, transparent)`, border: `1px solid color-mix(in srgb, ${color} 15%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon style={{ width: 16, height: 16, color }} />
        </div>
      </div>
      <p style={{ fontSize: 26, fontWeight: 900, color: 'var(--ink-primary)', margin: '0 0 4px', letterSpacing: '-0.03em' }}>{value}</p>
      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-secondary)', margin: '0 0 2px' }}>{label}</p>
      {sub && <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>{sub}</p>}
    </motion.div>
  )
}

function Section({ title, children }: any) {
  return (
    <div style={{ background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 16, overflow: 'hidden', marginBottom: 20 }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>{title}</h3>
      </div>
      <div style={{ padding: '20px' }}>{children}</div>
    </div>
  )
}

function CustomTooltip({ active, payload, label, isCurrency }: any) {
  if (!active || !payload?.length) return null
  return (
    <div style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 10, padding: '8px 12px', fontSize: 12, color: 'var(--ink-primary)' }}>
      <p style={{ fontSize: 11, color: 'var(--ink-secondary)', margin: '0 0 4px' }}>{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ fontSize: 13, fontWeight: 700, color: p.color || 'var(--ink-primary)', margin: 0 }}>
          {p.name}: {isCurrency ? formatCurrency(p.value) : p.value}
        </p>
      ))}
    </div>
  )
}

interface StageRow { id: string; name: string; order_index: number | null; win_probability: number | null; color: string | null }
interface DealRow { id: string; value: number | null; stage_id: string | null; stage_name: string | null; status: string | null; close_date: string | null; created_at: string | null }

export default function PipelineForecast() {
  const { workspace } = useAuth()
  const wsId = workspace?.id ?? null

  // Multi-pipeline CP4: the forecast is PER-PIPELINE (no cross-pipeline mixing).
  // pipelines + the selected one (derived: explicit → default → first; mirror CP3).
  const [selectedPid, setSelectedPid] = useState<string>('')
  const { data: pipelines = [] } = useQuery({
    queryKey: ['pipelines', wsId],
    queryFn: async () => {
      const { data } = await (supabase as any).from('pipelines').select('id,name,is_default').eq('workspace_id', wsId).order('is_default', { ascending: false }).order('created_at')
      return data || []
    },
    enabled: !!wsId,
  })
  const effectivePid: string = selectedPid || ((pipelines as any[]).find(p => p.is_default)?.id) || ((pipelines as any[])[0]?.id) || ''

  const { data: stages = [] } = useQuery<StageRow[]>({
    queryKey: ['forecast-stages', effectivePid],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('pipeline_stages')
        .select('id,name,order_index,win_probability,color')
        .eq('pipeline_id', effectivePid)
        .order('order_index')
      if (error) throw error
      return (data ?? []) as StageRow[]
    },
    enabled: !!effectivePid,
  })

  // Fetch the SELECTED pipeline's deals once → the forecast aggregation filters
  // open in JS, and the performance section derives won/lost from the same set.
  const { data: deals = [], isLoading } = useQuery<DealRow[]>({
    queryKey: ['forecast-deals', effectivePid],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('deals')
        .select('id,value,stage_id,stage_name,status,close_date,created_at')
        .eq('workspace_id', wsId)
        .eq('pipeline_id', effectivePid)
      if (error) throw error
      return (data ?? []) as DealRow[]
    },
    enabled: !!effectivePid,
  })

  const agg = useMemo(() => {
    // Lookup tables for the hybrid match: by id, and by lower(name).
    const byId = new Map<string, StageRow>()
    const byName = new Map<string, StageRow>()
    for (const s of stages) {
      byId.set(s.id, s)
      if (s.name) byName.set(s.name.toLowerCase(), s)
    }

    // Per-stage accumulators (in order_index order) + an Unassigned bucket.
    const rows = stages.map(s => ({
      key: s.id, name: s.name, win: s.win_probability ?? 0, color: s.color,
      count: 0, raw: 0, weighted: 0,
    }))
    const rowByStageId = new Map(rows.map(r => [r.key, r]))
    const unassigned = { key: '__unassigned__', name: 'Unassigned', win: 0, color: null as string | null, count: 0, raw: 0, weighted: 0 }

    // The forecast is forward-looking: ONLY open deals (won/lost excluded).
    const openDeals = deals.filter(d => d.status === 'open')
    for (const d of openDeals) {
      const v = Number(d.value) || 0
      // HYBRID: stage_id first (when it maps to a current stage), else stage_name.
      let stage: StageRow | undefined
      if (d.stage_id && byId.has(d.stage_id)) stage = byId.get(d.stage_id)
      else if (d.stage_name) stage = byName.get(d.stage_name.toLowerCase())
      const row = stage ? rowByStageId.get(stage.id)! : unassigned
      const win = stage ? (stage.win_probability ?? 0) : 0   // NULL win_probability → 0, no NaN
      row.count += 1
      row.raw += v
      row.weighted += v * win / 100
    }

    const stageRows = [...rows]
    if (unassigned.count > 0) stageRows.push(unassigned)
    const totalCount = openDeals.length
    const totalOpenValue = stageRows.reduce((a, r) => a + r.raw, 0)
    const totalWeighted = stageRows.reduce((a, r) => a + r.weighted, 0)
    return { stageRows, totalCount, totalOpenValue, totalWeighted }
  }, [stages, deals])

  // Win/loss performance over closed deals (Slice 2). Derived from the same
  // all-deals fetch; uses deal.value (the only money field; NULL → 0).
  const perf = useMemo(() => {
    let wonCount = 0, lostCount = 0, openCount = 0, totalWonValue = 0, totalLostValue = 0
    for (const d of deals) {
      const v = Number(d.value) || 0
      if (d.status === 'won') { wonCount += 1; totalWonValue += v }
      else if (d.status === 'lost') { lostCount += 1; totalLostValue += v }
      else if (d.status === 'open') openCount += 1
    }
    const closed = wonCount + lostCount
    const winRate = closed > 0 ? Math.round((wonCount / closed) * 100) : null   // no closed → N/A, not /0
    const avgWonValue = wonCount > 0 ? totalWonValue / wonCount : null           // no won → "—", not NaN
    const avgLostValue = lostCount > 0 ? totalLostValue / lostCount : null       // no lost → "—", not NaN
    return { wonCount, lostCount, openCount, totalWonValue, winRate, avgWonValue, avgLostValue, hasClosed: closed > 0 }
  }, [deals])

  // Slice 3: won value by month over the last 12 calendar months. Bucket key =
  // close_date (the close date), falling back to created_at when a won deal has
  // no close_date. Month key via string slice (YYYY-MM) — close_date is a DATE
  // and created_at an ISO timestamp, both prefixed YYYY-MM-DD, so no timezone drift.
  const trend = useMemo(() => {
    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const now = new Date()
    // continuous 12-month window: (now − 11 months) … now, no gaps
    const window: { key: string; label: string }[] = []
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      const label = MONTHS[d.getMonth()] + (d.getMonth() === 0 || i === 11 ? ` '${String(d.getFullYear()).slice(2)}` : '')
      window.push({ key, label })
    }
    const inWindow = new Set(window.map(m => m.key))
    const wonByMonth = new Map<string, number>()       // Slice 3: won value (close_date month)
    const inflowByMonth = new Map<string, number>()    // Slice 4: created-deal value (created_at month)
    const wonCountByMonth = new Map<string, number>()  // Slice 4: win-rate numerator
    const lostCountByMonth = new Map<string, number>() // Slice 4: win-rate denominator
    for (const d of deals) {
      const v = Number(d.value) || 0
      // INFLOW: every deal counts in its created_at month (pipeline inflow).
      if (d.created_at) {
        const mk = d.created_at.slice(0, 7)
        if (inWindow.has(mk)) inflowByMonth.set(mk, (inflowByMonth.get(mk) || 0) + v)
      }
      // WON value (unchanged from Slice 3) + won/lost counts, keyed by close_date
      // (fallback created_at) month.
      if (d.status === 'won' || d.status === 'lost') {
        const dateStr = d.close_date || d.created_at
        if (dateStr) {
          const mk = dateStr.slice(0, 7)
          if (inWindow.has(mk)) {
            if (d.status === 'won') {
              wonByMonth.set(mk, (wonByMonth.get(mk) || 0) + v)
              wonCountByMonth.set(mk, (wonCountByMonth.get(mk) || 0) + 1)
            } else {
              lostCountByMonth.set(mk, (lostCountByMonth.get(mk) || 0) + 1)
            }
          }
        }
      }
    }
    const series = window.map(m => ({
      month: m.label,
      Won: Math.round(wonByMonth.get(m.key) || 0),
      Inflow: Math.round(inflowByMonth.get(m.key) || 0),
    }))
    const totalWon = series.reduce((a, s) => a + s.Won, 0)
    const totalInflow = series.reduce((a, s) => a + s.Inflow, 0)
    // Win rate % per month — null where no closed deals (never fabricate a rate).
    const winRateSeries = window.map(m => {
      const w = wonCountByMonth.get(m.key) || 0
      const l = lostCountByMonth.get(m.key) || 0
      const closed = w + l
      return { month: m.label, rate: closed > 0 ? Math.round((w / closed) * 100) : null }
    })
    return {
      series, totalWon, totalInflow,
      hasData: totalWon > 0 || totalInflow > 0,           // show chart if EITHER has data
      winRateSeries, hasClosedAnyMonth: winRateSeries.some(s => s.rate !== null),
    }
  }, [deals])

  const chartData = useMemo(
    () => agg.stageRows.map(r => ({ name: r.name, Raw: Math.round(r.raw), Weighted: Math.round(r.weighted) })),
    [agg.stageRows],
  )

  const hasDeals = agg.totalCount > 0

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: '24px 28px 28px' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: 0 }}>Pipeline Forecast</h1>
        <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>
          Open pipeline by stage, weighted by win probability
        </p>
        {/* Multi-pipeline CP4: per-pipeline forecast — selector hidden for single-pipeline workspaces. */}
        {(pipelines as any[]).length > 1 && (
          <select value={effectivePid} onChange={e => setSelectedPid(e.target.value)}
            style={{ marginTop: 12, padding: '7px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, fontSize: 12, fontWeight: 600, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
            {(pipelines as any[]).map(p => <option key={p.id} value={p.id}>{p.name}{p.is_default ? ' (default)' : ''}</option>)}
          </select>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 20 }}>
        <StatCard label="Open pipeline" value={formatCurrency(agg.totalOpenValue)} sub={`${agg.totalCount} open deal${agg.totalCount === 1 ? '' : 's'}`} color={COLORS.primary} icon={Layers} />
        <StatCard label="Weighted forecast" value={formatCurrency(agg.totalWeighted)} sub="Σ value × win probability" color={COLORS.neon} icon={Target} />
        <StatCard label="Open deals" value={agg.totalCount} color={COLORS.amber} icon={TrendingUp} />
      </div>

      <Section title="Value by stage">
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-tertiary)', fontSize: 13 }}>Loading…</div>
        ) : !hasDeals ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <BarChart3 style={{ width: 26, height: 26, color: 'var(--ink-tertiary)', margin: '0 auto 10px', display: 'block' }} />
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>No open deals to forecast</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--hover-bg)" />
              <XAxis dataKey="name" tick={{ fill: 'var(--ink-tertiary)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--ink-tertiary)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip isCurrency />} cursor={{ fill: 'var(--hover-bg)' }} />
              <Bar name="Open value" dataKey="Raw" fill="var(--accent)" radius={[4, 4, 0, 0]} />
              <Bar name="Weighted" dataKey="Weighted" fill="var(--positive)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Section>

      {hasDeals && (
        <Section title="Stage breakdown">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 0.6fr 1fr 0.8fr 1fr', padding: '8px 12px', fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              <span>Stage</span>
              <span style={{ textAlign: 'right' }}>Deals</span>
              <span style={{ textAlign: 'right' }}>Open value</span>
              <span style={{ textAlign: 'right' }}>Win %</span>
              <span style={{ textAlign: 'right' }}>Weighted</span>
            </div>
            {agg.stageRows.map(r => (
              <div key={r.key} style={{ display: 'grid', gridTemplateColumns: '1.6fr 0.6fr 1fr 0.8fr 1fr', padding: '10px 12px', fontSize: 13, alignItems: 'center', background: 'var(--surface)', borderRadius: 8 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: 'var(--ink-primary)' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: r.color || 'var(--accent)', flexShrink: 0 }} />
                  {r.name}
                </span>
                <span style={{ textAlign: 'right', color: 'var(--ink-secondary)' }}>{r.count}</span>
                <span style={{ textAlign: 'right', color: 'var(--ink-secondary)' }}>{formatCurrency(r.raw)}</span>
                <span style={{ textAlign: 'right', color: 'var(--ink-tertiary)' }}>{r.key === '__unassigned__' ? '—' : `${r.win}%`}</span>
                <span style={{ textAlign: 'right', fontWeight: 700, color: 'var(--positive)' }}>{formatCurrency(r.weighted)}</span>
              </div>
            ))}
            <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 0.6fr 1fr 0.8fr 1fr', padding: '12px', fontSize: 13, fontWeight: 800, color: 'var(--ink-primary)', borderTop: '1px solid var(--border-subtle)', marginTop: 4 }}>
              <span>Total</span>
              <span style={{ textAlign: 'right' }}>{agg.totalCount}</span>
              <span style={{ textAlign: 'right' }}>{formatCurrency(agg.totalOpenValue)}</span>
              <span style={{ textAlign: 'right' }}>—</span>
              <span style={{ textAlign: 'right', color: 'var(--positive)' }}>{formatCurrency(agg.totalWeighted)}</span>
            </div>
          </div>
        </Section>
      )}

      {/* Slice 2: win/loss performance over closed deals. */}
      <Section title="Performance">
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-tertiary)', fontSize: 13 }}>Loading…</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: perf.hasClosed ? 0 : 14 }}>
              <StatCard label="Win rate" value={perf.winRate === null ? '—' : `${perf.winRate}%`} sub={perf.hasClosed ? `${perf.wonCount} won · ${perf.lostCount} lost` : 'No closed deals yet'} color={COLORS.neon} icon={Target} />
              <StatCard label="Total won" value={formatCurrency(perf.totalWonValue)} sub={`${perf.wonCount} deal${perf.wonCount === 1 ? '' : 's'}`} color={COLORS.primary} icon={TrendingUp} />
              <StatCard label="Avg won deal" value={perf.avgWonValue === null ? '—' : formatCurrency(perf.avgWonValue)} sub={`${perf.wonCount} won`} color={COLORS.amber} icon={Layers} />
              <StatCard label="Avg lost deal" value={perf.avgLostValue === null ? '—' : formatCurrency(perf.avgLostValue)} sub={`${perf.lostCount} lost`} color={COLORS.primary} icon={Layers} />
            </div>
            {/* count breakdown */}
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 16 }}>
              {([
                { label: 'Open', n: perf.openCount, color: 'var(--info)' },
                { label: 'Won', n: perf.wonCount, color: 'var(--positive)' },
                { label: 'Lost', n: perf.lostCount, color: 'var(--danger)' },
              ] as const).map(c => (
                <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: c.color }} />
                  <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>{c.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)' }}>{c.n}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </Section>

      {/* Slice 3 + 4: won value + pipeline inflow by month over the last 12 months. */}
      <Section title="Value over time">
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-tertiary)', fontSize: 13 }}>Loading…</div>
        ) : !trend.hasData ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <BarChart3 style={{ width: 26, height: 26, color: 'var(--ink-tertiary)', margin: '0 auto 10px', display: 'block' }} />
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>No deal activity in the last 12 months</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={trend.series}>
              <defs>
                <linearGradient id="wonGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--positive)" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="var(--positive)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="inflowGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--info)" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="var(--info)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--hover-bg)" />
              <XAxis dataKey="month" tick={{ fill: 'var(--ink-tertiary)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--ink-tertiary)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip isCurrency />} cursor={{ stroke: 'var(--border-default)' }} />
              <Area name="Inflow value" dataKey="Inflow" stroke="var(--info)" strokeWidth={2} fill="url(#inflowGradient)" />
              <Area name="Won value" dataKey="Won" stroke="var(--positive)" strokeWidth={2} fill="url(#wonGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
        {/* legend */}
        {trend.hasData && (
          <div style={{ display: 'flex', gap: 18, marginTop: 10, justifyContent: 'center' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-secondary)' }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--positive)' }} /> Won value
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-secondary)' }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--info)' }} /> Inflow value
            </span>
          </div>
        )}
      </Section>

      {/* Slice 4: win rate by month (small secondary metric). */}
      <Section title="Win rate by month">
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 30, color: 'var(--ink-tertiary)', fontSize: 13 }}>Loading…</div>
        ) : !trend.hasClosedAnyMonth ? (
          <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>Not enough closed deals for a monthly win rate.</p>
        ) : (
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={trend.winRateSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--hover-bg)" />
              <XAxis dataKey="month" tick={{ fill: 'var(--ink-tertiary)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fill: 'var(--ink-tertiary)', fontSize: 11 }} axisLine={false} tickLine={false} unit="%" />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'var(--hover-bg)' }} />
              <Bar name="Win rate" dataKey="rate" fill="var(--accent)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Section>
    </div>
  )
}
